import { useState, useEffect } from "react";
import { getApiUrl } from "../utils/api";
const API = getApiUrl();
import { QrCode, Key, Copy, Check, Upload, Trash2, Info } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { toast } from "sonner";


const PIX_KEY_TYPES = [
  { value: "cpf", label: "CPF" },
  { value: "cnpj", label: "CNPJ" },
  { value: "email", label: "E-mail" },
  { value: "phone", label: "Telefone" },
  { value: "random", label: "Chave Aleatória" }
];

export default function PixConfig({ site, onUpdate }) {
  const [pixConfig, setPixConfig] = useState({
    enabled: false,
    key_type: "cpf",
    key_value: "",
    holder_name: "",
    qr_code_image: null
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (site?.pix_config) {
      setPixConfig({
        enabled: site.pix_config.enabled || false,
        key_type: site.pix_config.key_type || "cpf",
        key_value: site.pix_config.key_value || "",
        holder_name: site.pix_config.holder_name || "",
        qr_code_image: site.pix_config.qr_code_image || null
      });
    }
  }, [site]);

  const handleSave = async () => {
    if (pixConfig.enabled && !pixConfig.key_value) {
      toast.error("Informe a chave PIX");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API}/sites/${site.site_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pix_config: pixConfig })
      });

      if (res.ok) {
        toast.success("Configurações de PIX salvas!");
        const updated = await res.json();
        if (onUpdate) onUpdate(updated);
      } else {
        throw new Error('Erro ao salvar');
      }
    } catch (error) {
      toast.error("Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  const handleQrCodeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Selecione uma imagem");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', 'pix_qrcode');

      const res = await fetch(`${API}/sites/${site.site_id}/upload-image?image_type=pix_qrcode`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setPixConfig(prev => ({ ...prev, qr_code_image: data.url }));
        toast.success("QR Code enviado!");
      } else {
        throw new Error('Erro no upload');
      }
    } catch (error) {
      toast.error("Erro ao enviar QR Code");
    } finally {
      setUploading(false);
    }
  };

  const removeQrCode = () => {
    setPixConfig(prev => ({ ...prev, qr_code_image: null }));
  };

  const formatPixKey = (type, value) => {
    if (type === "cpf") {
      return value.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    if (type === "phone") {
      return value.replace(/\D/g, '').replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return value;
  };

  return (
    <Card className="card-dashboard">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-serif text-xl text-[#6E0F1A] flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              PIX Manual
            </CardTitle>
            <CardDescription className="mt-1">
              Configure seu PIX para receber pagamentos diretamente
            </CardDescription>
          </div>
          <Switch
            checked={pixConfig.enabled}
            onCheckedChange={(checked) => setPixConfig(prev => ({ ...prev, enabled: checked }))}
          />
        </div>
      </CardHeader>
      
      {pixConfig.enabled && (
        <CardContent className="space-y-6">
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Como funciona:</p>
              <p>Quando o convidado escolher "Pagar com PIX", ele verá sua chave PIX e QR Code para fazer a transferência diretamente para você.</p>
            </div>
          </div>

          {/* Holder Name */}
          <div className="space-y-2">
            <Label className="text-[#6E0F1A]">Nome do Titular</Label>
            <Input
              value={pixConfig.holder_name}
              onChange={(e) => setPixConfig(prev => ({ ...prev, holder_name: e.target.value }))}
              placeholder="Nome que aparece na conta"
              className="border-[#F3C9CF] focus:border-[#B3121B]"
            />
          </div>

          {/* PIX Key Type */}
          <div className="space-y-2">
            <Label className="text-[#6E0F1A]">Tipo da Chave PIX</Label>
            <Select
              value={pixConfig.key_type}
              onValueChange={(value) => setPixConfig(prev => ({ ...prev, key_type: value }))}
            >
              <SelectTrigger className="border-[#F3C9CF] focus:border-[#B3121B]">
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent>
                {PIX_KEY_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* PIX Key Value */}
          <div className="space-y-2">
            <Label className="text-[#6E0F1A]">Chave PIX</Label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={pixConfig.key_value}
                onChange={(e) => setPixConfig(prev => ({ ...prev, key_value: e.target.value }))}
                placeholder={
                  pixConfig.key_type === "cpf" ? "000.000.000-00" :
                  pixConfig.key_type === "cnpj" ? "00.000.000/0000-00" :
                  pixConfig.key_type === "email" ? "seu@email.com" :
                  pixConfig.key_type === "phone" ? "(00) 00000-0000" :
                  "Sua chave aleatória"
                }
                className="pl-10 border-[#F3C9CF] focus:border-[#B3121B]"
              />
            </div>
          </div>

          {/* QR Code Upload */}
          <div className="space-y-2">
            <Label className="text-[#6E0F1A]">QR Code do PIX (opcional)</Label>
            <p className="text-sm text-gray-500 mb-2">
              Faça upload de uma imagem do QR Code gerado pelo seu banco
            </p>
            
            {pixConfig.qr_code_image ? (
              <div className="relative inline-block">
                <img
                  src={pixConfig.qr_code_image.startsWith('http') ? pixConfig.qr_code_image : `${API}${pixConfig.qr_code_image}`}
                  alt="QR Code PIX"
                  className="w-48 h-48 object-contain border rounded-lg bg-white p-2"
                />
                <button
                  onClick={removeQrCode}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-48 h-48 border-2 border-dashed border-[#F3C9CF] rounded-lg cursor-pointer hover:border-[#B3121B] transition-colors bg-[#FFFAF5]">
                <QrCode className="w-12 h-12 text-[#B3121B]/40 mb-2" />
                <span className="text-sm text-[#6E0F1A]/60">
                  {uploading ? "Enviando..." : "Clique para enviar"}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleQrCodeUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            )}
          </div>

          {/* Preview */}
          {pixConfig.key_value && (
            <div className="bg-[#FFFAF5] rounded-xl p-6 border border-[#F3C9CF]">
              <p className="text-sm text-[#6E0F1A]/60 mb-2">Prévia de como aparecerá:</p>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="font-medium text-[#6E0F1A] mb-1">
                  {pixConfig.holder_name || "Nome do Titular"}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  {PIX_KEY_TYPES.find(t => t.value === pixConfig.key_type)?.label}:
                </p>
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-3">
                  <code className="flex-1 text-lg font-mono text-[#6E0F1A]">
                    {formatPixKey(pixConfig.key_type, pixConfig.key_value)}
                  </code>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#B3121B] hover:bg-[#9A0F17] text-white"
          >
            {saving ? "Salvando..." : "Salvar Configurações de PIX"}
          </Button>
        </CardContent>
      )}
    </Card>
  );
}
